/**
 * Register Page — Launch-Ready
 *
 * Flow:
 *   1. Username, email, password fields
 *   2. Date of birth (validates 18+)
 *   3. Checkbox: "I confirm I am 18 years of age or older"
 *   4. CC BY-SA 4.0 informational notice (no checkbox — covered by TOS)
 *   5. Checkbox: "I agree to the Terms of Service and Privacy Policy" (with links)
 *   6. "Watch the tour" video banner (unobtrusive but visible)
 *
 * Place at: src/pages/RegisterPage.jsx
 */

import React, { useState, useMemo, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Sparkles, Mail, Lock, User, Eye, EyeOff, ArrowRight,
  Check, Calendar, Share2, Play, AlertTriangle
} from 'lucide-react';
import { Turnstile } from '@marsidev/react-turnstile';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui';
import { cn } from '../utils/cn';
import CampusPicker from '../components/CampusPicker';

// ============================================================================
// TURNSTILE CONFIG
// ============================================================================
// Site key is public (safe to ship in bundle). Secret key is server-side only.
// Falls back to Cloudflare's "always-pass" test key if env var is missing,
// so local dev and preview builds don't break. In production, set
// VITE_TURNSTILE_SITE_KEY at build time (Azure Static Web Apps → env vars).
// Docs: https://developers.cloudflare.com/turnstile/troubleshooting/testing/

const TURNSTILE_SITE_KEY =
  import.meta.env.VITE_TURNSTILE_SITE_KEY || '1x00000000000000000000AA';

// ============================================================================
// HELPERS
// ============================================================================

function calculateAge(dobString) {
  if (!dobString) return null;
  const dob = new Date(dobString + 'T00:00:00');
  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const m = today.getMonth() - dob.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) age--;
  return age;
}

function todayISO() {
  return new Date().toISOString().split('T')[0];
}

// ============================================================================
// VIDEO TOUR BANNER
// ============================================================================

const VideoTourBanner = () => {
  const [showModal, setShowModal] = useState(false);

  return (
    <>
      <button
        type="button"
        onClick={() => setShowModal(true)}
        className="w-full group flex items-center gap-3 p-3 rounded-xl bg-zinc-800/40 border border-zinc-700/50 hover:border-purple-500/30 hover:bg-purple-500/5 transition-all duration-200"
      >
        <div className="w-10 h-10 rounded-xl bg-purple-500/15 flex items-center justify-center flex-shrink-0 group-hover:bg-purple-500/25 transition-colors">
          <Play className="w-5 h-5 text-purple-400 ml-0.5" />
        </div>
        <div className="text-left flex-1 min-w-0">
          <p className="text-sm font-medium text-zinc-200 group-hover:text-white transition-colors">
            New here? Watch the 2-minute tour
          </p>
          <p className="text-xs text-zinc-500">See how the cosmic map, forking, and challenges work</p>
        </div>
        <ArrowRight className="w-4 h-4 text-zinc-600 group-hover:text-purple-400 transition-colors flex-shrink-0" />
      </button>

      {showModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
          onClick={() => setShowModal(false)}
        >
          <div
            className="w-full max-w-3xl bg-zinc-900 border border-zinc-700 rounded-2xl overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* 16:9 video container */}
            <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
              {/*
                ═══════════════════════════════════════════════════════
                REPLACE THIS PLACEHOLDER WITH YOUR VIDEO

                Self-hosted:
                  <video src="/videos/enumé-tour.mp4" controls autoPlay
                    className="absolute inset-0 w-full h-full object-cover" />

                YouTube:
                  <iframe src="https://www.youtube.com/embed/VIDEO_ID?autoplay=1"
                    allow="autoplay; encrypted-media" allowFullScreen
                    className="absolute inset-0 w-full h-full" />

                Vimeo:
                  <iframe src="https://player.vimeo.com/video/VIDEO_ID?autoplay=1"
                    allow="autoplay; fullscreen" allowFullScreen
                    className="absolute inset-0 w-full h-full" />
                ═══════════════════════════════════════════════════════
              */}
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-zinc-800">
                <div className="w-16 h-16 rounded-2xl bg-purple-500/20 flex items-center justify-center mb-4">
                  <Play className="w-8 h-8 text-purple-400 ml-1" />
                </div>
                <p className="text-zinc-400 text-sm">Tour video coming soon</p>
                <p className="text-zinc-600 text-xs mt-1">Check back shortly</p>
              </div>
            </div>
            <div className="px-5 py-3 bg-zinc-800/50 border-t border-zinc-700/50 flex items-center justify-between">
              <p className="text-xs text-zinc-500">2 min · How Enumé works</p>
              <button onClick={() => setShowModal(false)}
                className="text-xs text-zinc-400 hover:text-white px-3 py-1.5 rounded-lg hover:bg-zinc-700/50 transition-colors">
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// ============================================================================
// MAIN REGISTER PAGE
// ============================================================================

export const RegisterPage = () => {
  const navigate = useNavigate();
  const { register, isAuthenticated, error: authError, clearError } = useAuth();

  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  const turnstileRef = useRef(null);
  const [campusId, setCampusId] = useState(undefined);

  // Derived
  const age = useMemo(() => calculateAge(dateOfBirth), [dateOfBirth]);
  const isUnderage = age !== null && age < 18;
  const isOldEnough = age !== null && age >= 18;
  const passwordValid = password.length >= 8;
  const passwordsMatch = password === confirmPassword;

  const canSubmit =
    username.length >= 3 &&
    email.length > 0 &&
    passwordValid &&
    passwordsMatch &&
    isOldEnough &&
    ageConfirmed &&
    agreedToTerms &&
    !!turnstileToken &&
    !loading;

  // Redirect if already authenticated
  React.useEffect(() => {
    if (isAuthenticated) navigate('/dashboard');
  }, [isAuthenticated, navigate]);

  // Clear errors on input change
  React.useEffect(() => {
    if (error || authError) {
      setError('');
      clearError?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [username, email, password, confirmPassword, dateOfBirth]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    setLoading(true);
    setError('');

    try {
      await register(email, password, username, dateOfBirth, turnstileToken);
      navigate('/dashboard');
    } catch (err) {
      setError(err.message || 'Registration failed. Please try again.');
      // Turnstile tokens are single-use — reset the widget so the user can
      // retry without reloading the page.
      turnstileRef.current?.reset();
      setTurnstileToken('');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4 py-8">
      {/* Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 -left-1/4 w-1/2 h-1/2 bg-purple-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 -right-1/4 w-1/2 h-1/2 bg-indigo-500/10 rounded-full blur-3xl" />
      </div>

      <div className="relative w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <button onClick={() => navigate('/')} className="inline-flex items-center gap-2">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-600 to-indigo-600 flex items-center justify-center">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <span className="text-2xl font-bold text-white">Enumé</span>
          </button>
        </div>

        {/* Video tour — above the form, unobtrusive */}
        <div className="mb-6">
          <VideoTourBanner />
        </div>

        {/* Form card */}
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-6 sm:p-8 backdrop-blur-sm">
          <h1 className="text-2xl font-bold text-white mb-2">Create account</h1>
          <p className="text-zinc-500 mb-6 text-sm">Join the creative constellation</p>

          <form onSubmit={handleSubmit} className="space-y-4">

            {/* ── Username ─────────────────────────────────────────── */}
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-1.5">Username</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
                  placeholder="your_username"
                  className="w-full bg-zinc-800/50 border border-zinc-700 rounded-xl pl-11 pr-4 py-3 text-white placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500/50 transition-all"
                  maxLength={20}
                  required
                />
              </div>
              <p className="mt-1 text-xs text-zinc-500">Letters, numbers, underscores. Min 3 characters.</p>
            </div>

            {/* ── Email ─────────────────────────────────────────────── */}
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-1.5">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full bg-zinc-800/50 border border-zinc-700 rounded-xl pl-11 pr-4 py-3 text-white placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500/50 transition-all"
                  required
                />
              </div>
            </div>

            {/* ── Password ─────────────────────────────────────────── */}
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-1.5">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Minimum 8 characters"
                  className="w-full bg-zinc-800/50 border border-zinc-700 rounded-xl pl-11 pr-12 py-3 text-white placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500/50 transition-all"
                  required
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300 transition-colors">
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {password && (
                <div className={cn('mt-1 flex items-center gap-1.5 text-xs', passwordValid ? 'text-emerald-400' : 'text-zinc-500')}>
                  <Check className={cn('w-3 h-3', passwordValid ? 'opacity-100' : 'opacity-30')} />
                  At least 8 characters
                </div>
              )}
            </div>

            {/* ── Confirm Password ─────────────────────────────────── */}
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-1.5">Confirm Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Re-enter password"
                  className={cn(
                    'w-full bg-zinc-800/50 border rounded-xl pl-11 pr-4 py-3 text-white placeholder:text-zinc-500 focus:outline-none focus:ring-2 transition-all',
                    confirmPassword && !passwordsMatch
                      ? 'border-red-500/50 focus:ring-red-500/50'
                      : 'border-zinc-700 focus:ring-purple-500/50 focus:border-purple-500/50'
                  )}
                  required
                />
              </div>
              {confirmPassword && !passwordsMatch && (
                <p className="mt-1 text-xs text-red-400">Passwords do not match</p>
              )}
            </div>

            {/* ── Date of Birth ─────────────────────────────────────── */}
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-1.5">Date of Birth</label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                <input
                  type="date"
                  value={dateOfBirth}
                  onChange={(e) => setDateOfBirth(e.target.value)}
                  max={todayISO()}
                  className={cn(
                    'w-full bg-zinc-800/50 border rounded-xl pl-11 pr-4 py-3 text-white focus:outline-none focus:ring-2 transition-all',
                    '[color-scheme:dark]',
                    isUnderage
                      ? 'border-red-500/50 focus:ring-red-500/50'
                      : 'border-zinc-700 focus:ring-purple-500/50 focus:border-purple-500/50'
                  )}
                  required
                />
              </div>
              {isUnderage && (
                <div className="mt-2 p-2.5 rounded-lg bg-red-500/10 border border-red-500/20">
                  <p className="text-xs text-red-400 flex items-center gap-1.5">
                    <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
                    You must be 18 or older to create an account.
                  </p>
                </div>
              )}
              <p className="mt-1 text-xs text-zinc-500">Used only to verify your age. Not displayed publicly.</p>
            </div>

            {/* ── Campus ───────────────────────────────────────────── */}
            <CampusPicker
              value={campusId}
              onChange={setCampusId}
            />

            {/* ── Age Confirmation Checkbox ─────────────────────────── */}
            {isOldEnough && (
              <label className="flex items-start gap-3 cursor-pointer group">
                <div className="mt-0.5 flex-shrink-0">
                  <input
                    type="checkbox"
                    checked={ageConfirmed}
                    onChange={(e) => setAgeConfirmed(e.target.checked)}
                    className="sr-only"
                  />
                  <div className={cn(
                    'w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all',
                    ageConfirmed
                      ? 'bg-purple-600 border-purple-600'
                      : 'border-zinc-600 group-hover:border-zinc-400'
                  )}>
                    {ageConfirmed && <Check className="w-3.5 h-3.5 text-white" />}
                  </div>
                </div>
                <span className="text-sm text-zinc-300">
                  I confirm I am <span className="text-white font-medium">18 years of age or older</span>
                </span>
              </label>
            )}

            {/* ── CC BY-SA 4.0 Notice (informational) ──────────────── */}
            <div className="p-3 rounded-xl bg-purple-500/5 border border-purple-500/20">
              <div className="flex gap-2.5">
                <Share2 className="w-4 h-4 text-purple-400 mt-0.5 flex-shrink-0" />
                <div className="text-xs text-zinc-400 leading-relaxed">
                  <span className="text-zinc-200 font-medium">Creative Commons:</span> Everything you upload
                  will be licensed under{' '}
                  <a href="https://creativecommons.org/licenses/by-sa/4.0/" target="_blank" rel="noopener noreferrer"
                    className="text-purple-400 hover:text-purple-300 underline underline-offset-2">CC BY-SA 4.0</a>.
                  Anyone can fork, remix, and build on your work (with credit). This license is irrevocable.{' '}
                  <Link to="/terms#1.3" className="text-purple-400 hover:text-purple-300 underline underline-offset-2">
                    Learn more
                  </Link>
                </div>
              </div>
            </div>

            {/* ── TOS Agreement Checkbox ────────────────────────────── */}
            <label className="flex items-start gap-3 cursor-pointer group">
              <div className="mt-0.5 flex-shrink-0">
                <input
                  type="checkbox"
                  checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)}
                  className="sr-only"
                />
                <div className={cn(
                  'w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all',
                  agreedToTerms
                    ? 'bg-purple-600 border-purple-600'
                    : 'border-zinc-600 group-hover:border-zinc-400'
                )}>
                  {agreedToTerms && <Check className="w-3.5 h-3.5 text-white" />}
                </div>
              </div>
              <span className="text-sm text-zinc-300">
                I agree to the{' '}
                <Link to="/terms" target="_blank" className="text-purple-400 hover:text-purple-300 underline underline-offset-2">
                  Terms of Service
                </Link>,{' '}
                <Link to="/privacy" target="_blank" className="text-purple-400 hover:text-purple-300 underline underline-offset-2">
                  Privacy Policy
                </Link>, and{' '}
                <Link to="/dmca" target="_blank" className="text-purple-400 hover:text-purple-300 underline underline-offset-2">
                  DMCA Policy
                </Link>
              </span>
            </label>

            {/* ── Error display ─────────────────────────────────────── */}
            {(error || authError) && (
              <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-xl">
                <p className="text-sm text-red-400">{error || authError}</p>
              </div>
            )}

            {/* ── Turnstile (bot check) ────────────────────────────── */}
            {/*
              Cloudflare Turnstile — privacy-first, invisible for most users.
              Runs behavioral analysis in the background and emits a token.
              We verify the token server-side in auth.py before creating the account.
              Widget auto-challenges interactively only if the visitor looks suspicious.
            */}
            <div className="flex justify-center">
              <Turnstile
                ref={turnstileRef}
                siteKey={TURNSTILE_SITE_KEY}
                options={{
                  theme: 'dark',
                  size: 'flexible',
                  appearance: 'always',
                }}
                onSuccess={(token) => setTurnstileToken(token)}
                onExpire={() => setTurnstileToken('')}
                onError={() => setTurnstileToken('')}
              />
            </div>

            {/* ── Submit ───────────────────────────────────────────── */}
            <Button
              type="submit"
              loading={loading}
              disabled={!canSubmit}
              className="w-full py-3"
              icon={ArrowRight}
              iconPosition="right"
            >
              Create Account
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-zinc-500 text-sm">
              Already have an account?{' '}
              <Link to="/login" className="text-purple-400 hover:text-purple-300 font-medium transition-colors">
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;